import { useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import {
  DEPARTMENT_LABEL,
  PROFILE_RANGES,
  ROLE_LABEL,
  STAGE_LABEL,
  mayChangePhoto,
  type ProfileRange,
} from '@/domain';
import { useProfile, useRemoveAvatar, useSetAvatar } from '@/application/usecases/useAdmin';
import { useSession, useSignOut } from '@/application/usecases/useSession';
import { ChangePasswordDialog } from '@/features/auth/ChangePasswordDialog';
import { useConfirmAction } from '@/features/shell/useConfirmAction';
import { WhatsNewSheet } from '@/features/release/WhatsNewSheet';
import { formatInstant } from '@/shared/format/instant';
import { formatVersion } from '@/shared/format/version';
import { failureWords } from '@/shared/failure/failure';
import { strings } from '@/shared/strings';
import { PageLoading } from '@/ui/feedback/Loading';
import { CameraIcon, KeyIcon, SignOutIcon } from '@/ui/icons';
import { Button } from '@/ui/primitives/Button';
import { toast } from '@/ui/primitives/toastStore';
import { Avatar } from './Avatar';
import { useDeviceSetting } from '@/application/usecases/useDeviceSetting';
import { HAPTICS_SETTING, haptic, hapticsSupported, setHapticsEnabled } from '@/ui/motion/haptics';
import styles from './ProfileScreen.module.css';

const RANGE_LABEL: Record<ProfileRange, string> = {
  all: strings.profile.allTime,
  today: strings.profile.today,
  week: strings.profile.week,
  month: strings.profile.month,
};

/**
 * A person's profile. Request of 4 October 2026 (second), section 7.
 *
 * Who they are, how to reach them, their own counts for a period, what they are
 * on now and what they did last; for their own profile, the photograph, the
 * password, the app version and signing out. `/profile` is your own and opens
 * from your name in the menu; `/profile/{id}` is somebody else's, which only
 * management may open, and the server says so to anybody else.
 *
 * Beside the version, a holder of `admin` has What's new: every version and
 * what changed in it. The request of 5 October 2026 (third), section 8.
 *
 * A person changes their own photograph and password here and nothing else:
 * role, department and contact details are management's, on the team screen.
 *
 * Removing a photograph is asked about first, for the roles that are asked:
 * the request of 5 October 2026 (third), section 2. Choosing a new one is not,
 * because picking the file is the deliberate step, and neither is signing out,
 * which changes nothing in the lab.
 */
export function ProfileScreen() {
  const { userId: param } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { data: session } = useSession();
  const signOut = useSignOut();
  const me = session?.user.id ?? '';
  const userId = param ?? me;
  const own = userId === me;
  const isAdmin = session?.permissions.includes('admin') === true;

  // All time first and by default: the lab's request of 17 October 2026, item 4.
  const [range, setRange] = useState<ProfileRange>('all');
  const profile = useProfile(userId === '' ? undefined : userId, range);
  const setAvatar = useSetAvatar();
  const removeAvatar = useRemoveAvatar();
  const ask = useConfirmAction();
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [whatsNewOpen, setWhatsNewOpen] = useState(false);
  const photoInput = useRef<HTMLInputElement>(null);

  if (profile.isPending) return <PageLoading label={strings.profile.loading} />;
  if (profile.isError || profile.data === undefined) {
    return <p className={styles.error}>{failureWords(profile.error, strings.errors.server)}</p>;
  }

  const { user, summary, current, recent } = profile.data;
  const toCases = session?.views.includes('users') === true ? () => void navigate(`/users/${user.id}`) : undefined;
  const mayEditPhoto = mayChangePhoto(me, user.id, isAdmin);

  function upload(file: File) {
    setAvatar.mutate(
      { userId: user.id, file },
      {
        onSuccess: () => {
          toast(strings.profile.photoSaved);
          void profile.refetch();
        },
        onError: (error: Error) => toast(failureWords(error)),
      },
    );
  }

  return (
    <div className={styles.page}>
      <section className={styles.card} aria-labelledby="profile-name">
        <div className={styles.identity}>
          <Avatar name={user.name} url={user.avatarUrl} size={84} />
          <div className={styles.who}>
            <h1 id="profile-name">{user.name}</h1>
            <p className={styles.title}>{user.title}</p>
            <p className={styles.role}>
              {ROLE_LABEL[user.role]} · {DEPARTMENT_LABEL[user.department]}
            </p>
            <div className={styles.badges}>
              {user.isSupervisor && (
                <span className={styles.badge}>{strings.profile.supervisor}</span>
              )}
              {user.isIntakeOperator && (
                <span className={styles.badge}>{strings.profile.intake}</span>
              )}
              {user.disabled && (
                <span className={`${styles.badge} ${styles.off}`}>{strings.profile.disabled}</span>
              )}
            </div>
          </div>
        </div>

        {mayEditPhoto && (
          <div className={styles.photoActions}>
            <input
              ref={photoInput}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              aria-label={strings.profile.photoLabel}
              className={styles.fileInput}
              onChange={(event) => {
                const picked = event.target.files?.[0];
                if (picked !== undefined) upload(picked);
                event.target.value = '';
              }}
            />
            <Button
              icon={<CameraIcon />}
              disabled={setAvatar.isPending}
              onClick={() => photoInput.current?.click()}
            >
              {user.avatarUrl === null ? strings.profile.addPhoto : strings.profile.changePhoto}
            </Button>
            {user.avatarUrl !== null && (
              <Button
                variant="text"
                disabled={removeAvatar.isPending}
                onClick={() =>
                  ask(
                    strings.confirm.removeProfilePhoto(own ? null : user.name),
                    () =>
                      removeAvatar.mutateAsync(user.id).then(() => {
                        toast(strings.profile.photoRemoved);
                        void profile.refetch();
                      }),
                    {
                      title: strings.profile.removePhoto,
                      confirmLabel: strings.profile.removePhoto,
                    },
                  )
                }
              >
                {strings.profile.removePhoto}
              </Button>
            )}
          </div>
        )}

        <dl className={styles.facts}>
          <div>
            <dt>{strings.profile.phone}</dt>
            <dd>
              {user.phone === null ? (
                strings.profile.notSet
              ) : (
                <a href={`tel:${user.phone.replace(/[^\d+]/g, '')}`}>{user.phone}</a>
              )}
            </dd>
          </div>
          <div>
            <dt>{strings.profile.email}</dt>
            <dd>
              {user.email === null ? (
                strings.profile.notSet
              ) : (
                <a href={`mailto:${user.email}`}>{user.email}</a>
              )}
            </dd>
          </div>
          <div>
            <dt>{strings.profile.memberSince}</dt>
            <dd>{formatInstant(user.createdAt).slice(0, 10)}</dd>
          </div>
          {user.lastLoginAt !== null && (
            <div>
              <dt>{strings.profile.lastSignIn}</dt>
              <dd>{formatInstant(user.lastLoginAt)}</dd>
            </div>
          )}
        </dl>
        {!own && isAdmin && (
          <Link className={styles.link} to={`/users/${user.id}`}>
            {strings.profile.editInUsers}
          </Link>
        )}
      </section>

      {/* Absent for a technician: the server leaves the counts out. */}
      {summary !== null && (
        <section className={styles.card} aria-labelledby="profile-work">
          <div className={styles.head}>
            <h2 id="profile-work">{strings.profile.work}</h2>
            <div className={styles.seg} role="group" aria-label={strings.profile.periodLabel}>
              {PROFILE_RANGES.map((option) => (
                <button
                  key={option}
                  type="button"
                  aria-pressed={range === option}
                  className={range === option ? styles.on : undefined}
                  onClick={() => setRange(option)}
                >
                  {RANGE_LABEL[option]}
                </button>
              ))}
            </div>
          </div>
          {/* Each opens the person's cases, for a reader who holds Users. The lab's request of 17 October 2026, item 1. */}
          <div className={styles.stats}>
            <Stat label={strings.profile.casesWorked} value={summary.casesWorked} onPress={toCases} />
            <Stat label={strings.profile.teethWorked} value={summary.teethWorked} onPress={toCases} />
            <Stat label={strings.profile.jobOrdersCreated} value={summary.jobOrdersCreated} onPress={toCases} />
          </div>
          <p className={styles.hint}>{strings.profile.tasks(summary.tasksCompleted)}</p>
        </section>
      )}

      <section className={styles.card} aria-labelledby="profile-current">
        <h2 id="profile-current">{strings.profile.current}</h2>
        {current.length === 0 ? (
          <p className={styles.hint}>{strings.profile.nothingCurrent}</p>
        ) : (
          <ul className={styles.list}>
            {current.map((task) => (
              <li key={task.kitId}>
                <Link to={`/queue/${task.kitId}`} className={styles.task}>
                  <b>{task.jobOrderNo}</b>
                  <span>{STAGE_LABEL[task.stage]}</span>
                  <span className={task.running ? styles.running : styles.paused}>
                    {task.running ? strings.profile.running : strings.profile.paused}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className={styles.card} aria-labelledby="profile-recent">
        <h2 id="profile-recent">{strings.profile.recent}</h2>
        {recent.length === 0 ? (
          <p className={styles.hint}>{strings.profile.nothingRecent}</p>
        ) : (
          <ul className={styles.list}>
            {recent.map((row) => (
              <li key={row.id} className={styles.activity}>
                <span>
                  {row.action} {row.target}
                </span>
                <time dateTime={row.createdAt}>{formatInstant(row.createdAt)}</time>
              </li>
            ))}
          </ul>
        )}
      </section>

      {own && (
        <section className={styles.card} aria-labelledby="profile-account">
          <h2 id="profile-account">{strings.profile.account}</h2>
          <div className={styles.accountActions}>
            <Button icon={<KeyIcon />} onClick={() => setPasswordOpen(true)}>
              {strings.profile.changePassword}
            </Button>
            <Button
              icon={<SignOutIcon />}
              onClick={() =>
                signOut.mutate(undefined, { onSuccess: () => void navigate('/login') })
              }
            >
              {strings.profile.signOut}
            </Button>
          </div>
          {hapticsSupported() && <HapticsSetting />}
          {/*
           * Everybody reads the version, as the sign in screen and the menu
           * show it; what changed in each one is for holders of `admin`. The
           * request of 5 October 2026 (third), section 8.
           */}
          <div className={styles.versionRow}>
            <p className={styles.version}>
              {strings.profile.version}: <span data-testid="app-version">{formatVersion()}</span>
            </p>
            {isAdmin && (
              <Button variant="text" onClick={() => setWhatsNewOpen(true)}>
                {strings.versions.open}
              </Button>
            )}
          </div>
        </section>
      )}

      {passwordOpen && <ChangePasswordDialog onClose={() => setPasswordOpen(false)} />}
      {whatsNewOpen && <WhatsNewSheet onClose={() => setWhatsNewOpen(false)} />}
    </div>
  );
}

/**
 * A light vibration on a scan, a Mark done and a flag, on this device. Request
 * of 5 October 2026, section 5. Only where the device can vibrate at all, which
 * leaves iPhones out: they give a web page none.
 */
function HapticsSetting() {
  const [on, setOn] = useDeviceSetting(HAPTICS_SETTING, true);
  return (
    <label className={styles.setting}>
      <input
        type="checkbox"
        checked={on}
        onChange={(event) => {
          setOn(event.target.checked);
          setHapticsEnabled(event.target.checked);
          if (event.target.checked) haptic('success');
        }}
      />
      <span>
        {strings.profile.haptics}
        <small>{strings.profile.hapticsHint}</small>
      </span>
    </label>
  );
}

function Stat({ label, value, onPress }: { label: string; value: number; onPress?: () => void }) {
  const content = (
    <>
      <b>{value}</b>
      <span>{label}</span>
    </>
  );
  if (onPress === undefined) return <div className={styles.stat}>{content}</div>;
  return (
    <button type="button" className={`${styles.stat} ${styles.statPress}`} onClick={onPress}>
      {content}
    </button>
  );
}
